import type { Voice } from "@/hooks/voice"
import { ONBOARDING_LANGUAGE_OPTIONS } from "@/lib/onboarding/languages"

const KNOWN_LANGUAGE_LABELS = new Map<string, string>(
  ONBOARDING_LANGUAGE_OPTIONS.map((language) => [language.value, language.label]),
)
const LANGUAGE_DISPLAY_NAMES =
  typeof Intl !== "undefined" && typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en"], { type: "language" })
    : null

export const UNKNOWN_LANGUAGE_VALUE = "__unknown__"

export function getVoiceLanguages(voice: Voice): string[] {
  return voice.languages
}

export function getVoiceLanguageLabel(code: string): string {
  const normalized = code.toLowerCase()
  return (
    KNOWN_LANGUAGE_LABELS.get(normalized) ??
    LANGUAGE_DISPLAY_NAMES?.of(normalized) ??
    normalized.toUpperCase()
  )
}

export function buildVoiceLanguageOptions(voices: Voice[]) {
  const available = new Map<string, string>()
  let hasUnknown = false

  for (const voice of voices) {
    const languages = getVoiceLanguages(voice)
    if (languages.length === 0) {
      hasUnknown = true
      continue
    }
    for (const languageCode of languages) {
      const code = languageCode.toLowerCase()
      if (code && !available.has(code)) {
        available.set(code, getVoiceLanguageLabel(code))
      }
    }
  }

  const knownOptions = ONBOARDING_LANGUAGE_OPTIONS.filter((item) =>
    available.has(item.value),
  )
  const knownCodes = new Set<string>(knownOptions.map((item) => item.value))
  const extraOptions = Array.from(available.entries())
    .filter(([code]) => !knownCodes.has(code))
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const options: Array<{ value: string; label: string }> = [
    ...knownOptions,
    ...extraOptions,
  ]
  if (hasUnknown) {
    options.push({ value: UNKNOWN_LANGUAGE_VALUE, label: "Unknown" })
  }
  return options
}
